import { Dispatch, SetStateAction, useState } from "react";
import { Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import TableCellComponent from "components/table/TableCellComponent";
import TableRowComponent from "components/table/TableRowComponent";
import { HEADER_4BUNDLE_PRICE_SETTING } from "constants/data";
import { USER_TYPE_LIST } from "constants/user-type";
import ButtonComponent from "components/button/ButtonComponent";
import type { PriceInfo, PriceUpdatePayload } from "../types/price-info";

interface FourBundlePriceTableProps {
  prices: PriceInfo[];
  setPrices: Dispatch<SetStateAction<PriceInfo[]>>;
  onUpdate: (data: PriceUpdatePayload) => Promise<void>;
}

const NO_SETTING = "本部/企業設定なし";

/**
 * FourBundlePriceTable component renders a table for price settings.
 * @param {object} props - The component props
 * @param {PriceInfo[]} props.prices - The array of price information
 */
export default function FourBundlePriceTable({
  prices,
  setPrices,
  onUpdate,
}: FourBundlePriceTableProps) {
  const [error, setError] = useState<Record<string, boolean>>({});

  const MAX_LENGTH = 10;
  const userType: string | null = localStorage.getItem("user_type");

  const validator = (
    field: keyof PriceInfo,
    index: number,
    inputValue: string | number,
  ): boolean => {
    if (inputValue.toString().length > MAX_LENGTH) {
      setError((prev) => ({ ...prev, [`${field}-${index}`]: true }));
      return true;
    }
    setError((prev) => ({ ...prev, [`${field}-${index}`]: false }));
    return false;
  };

  const handleBlur = async (
    field: keyof PriceInfo,
    index: number,
    e: React.FocusEvent<HTMLDivElement>,
  ) => {
    const inputValue = e.target.innerText.replace(/,/g, "").trim();

    if (inputValue === NO_SETTING) return;

    // Cleared input → send column_delete_flag and show NO_SETTING.
    if (inputValue === "") {
      e.target.innerText = NO_SETTING;
      await onUpdate({
        item_cd: prices[index].item_cd,
        [field]: 0,
        column_delete_flag: 1,
      });
      return;
    }

    const numericValue = Number(inputValue);
    if (Number.isNaN(numericValue)) {
      e.target.innerText = prices[index][field]?.toLocaleString() || "0";
      return;
    }

    const oldValue = prices[index][field];
    const oldNumeric = oldValue == null ? null : Number(oldValue);
    if (oldNumeric === numericValue) {
      e.target.innerText = numericValue.toLocaleString();
      return;
    }

    if (validator(field, index, numericValue)) {
      return;
    }

    e.target.innerText = numericValue.toLocaleString();

    await onUpdate({
      item_cd: prices[index].item_cd,
      [field]: numericValue,
    });
  };

  const returnToHQSetting = async (index: number) => {
    const currentFlag = prices[index].delete_flag;
    const confirm = window.confirm("本部に戻しますか？");

    if (confirm) {
      const newPrices = [...prices];
      newPrices[index] = {
        ...newPrices[index],
        delete_flag: Number(!newPrices[index].delete_flag),
      };
      await onUpdate({
        item_cd: newPrices[index].item_cd,
        delete_flag: newPrices[index].delete_flag,
      });
    } else {
      setPrices((prevPrices) => {
        const restoredPrices = [...prevPrices];
        restoredPrices[index] = { ...restoredPrices[index], delete_flag: currentFlag };
        return restoredPrices;
      });
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    e.target.innerText = e.target.innerText.replace(/,/g, "");
    e.currentTarget.dataset.previousValue = e.currentTarget.innerText;
  };

  const handleOnInput = (
    e: React.FormEvent<HTMLDivElement>,
    field: keyof PriceInfo,
    index: number,
  ) => {
    const targetValue = e.currentTarget;
    const selection = window.getSelection();
    const range = selection?.getRangeAt(0);
    const offset = range?.startOffset || 0;

    targetValue.innerText = targetValue.innerText.replace(/[^0-9]/g, "");
    if (!targetValue.innerText.length) return;

    const newRange = document.createRange();
    if (targetValue.childNodes[0]) {
      newRange.setStart(targetValue.childNodes[0], Math.min(offset, targetValue.innerText.length));
      newRange.collapse(true);

      selection?.removeAllRanges();
      selection?.addRange(newRange);
    }
    validator(field, index, targetValue.innerText);
  };

  return (
    <TableContainer
      component={Table}
      sx={{
        boxShadow: "none",
        width: "100%",
        minWidth: 1421,
        borderCollapse: "unset",
        borderSpacing: "2px 1px",
      }}
    >
      <TableHead>
        <TableRowComponent data={HEADER_4BUNDLE_PRICE_SETTING} type="header" />
      </TableHead>
      <TableBody>
        {prices.map((price, index) => {
          const isTireCategoryW = price.tire_category === "w";
          const tirePrice = price.tire_4_price;
          const wheelPrice = price.winter_wheel_4_price;

          const getPriceColor = (honbuFlag: number) => {
            if (userType === USER_TYPE_LIST.headquarters.value) return "black";
            return honbuFlag === 1 ? "black" : "red";
          };

          return (
            <TableRow key={price.item_cd}>
              <TableCellComponent
                sx={{
                  backgroundColor: "var(--offwhite-400)",
                  border: "2px solid var(--silver-150)",
                }}
              >
                {price.item_cd}
              </TableCellComponent>
              <TableCellComponent
                sx={{
                  backgroundColor: "var(--offwhite-400)",
                  border: "2px solid var(--silver-150)",
                }}
              >
                {price.brand_cd}
              </TableCellComponent>
              <TableCellComponent
                sx={{
                  backgroundColor: "var(--offwhite-400)",
                  border: "2px solid var(--silver-150)",
                  fontWeight: "regular",
                  textAlign: "left",
                }}
              >
                {`${price?.maker_name ?? ""} ${price?.brand_name ?? ""}`}
              </TableCellComponent>
              <TableCellComponent
                sx={{
                  backgroundColor: "var(--offwhite-400)",
                  border: "2px solid var(--silver-150)",
                  fontWeight: "regular",
                  textAlign: "center",
                }}
              >
                {price.tire_size}
              </TableCellComponent>
              <TableCellComponent
                key={`tire_4_price-${price.item_cd}-${tirePrice ?? "null"}-${price.delete_flag}`}
                sx={{
                  backgroundColor: "var(--white)",
                  border: !error[`tire_4_price-${index}`]
                    ? "2px solid var(--silver-150)"
                    : "2px solid var(--red-800)",
                  fontWeight: "regular",
                  textAlign: "center",
                  lineHeight: "48px",
                  align: "center",
                  color: getPriceColor(price.tire_4_price_store_login_honbu_flag),
                  maxWidth: "136px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  outline: "none",
                }}
                onFocus={handleFocus}
                contentEditable
                suppressContentEditableWarning
                onInput={(e) => handleOnInput(e, "tire_4_price", index)}
                onBlur={(e) => handleBlur("tire_4_price", index, e)}
              >
                {tirePrice != null ? tirePrice.toLocaleString() : NO_SETTING}
              </TableCellComponent>

              {isTireCategoryW ? (
                <TableCellComponent
                  sx={{
                    backgroundColor: "var(--white)",
                    border: !error[`winter_wheel_4_price-${index}`]
                      ? "2px solid var(--silver-150)"
                      : "2px solid var(--red-800)",
                    textAlign: "center",
                    align: "center",
                    color: getPriceColor(price.winter_wheel_4_price_store_login_honbu_flag),
                    maxWidth: "212px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    outline: "none",
                  }}
                  onFocus={handleFocus}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={(e) => handleOnInput(e, "winter_wheel_4_price", index)}
                  onBlur={(e) => handleBlur("winter_wheel_4_price", index, e)}
                  value={wheelPrice}
                >
                  {wheelPrice != null ? wheelPrice.toLocaleString() : ""}
                </TableCellComponent>
              ) : (
                <TableCellComponent
                  sx={{
                    backgroundColor: "var(--offwhite-400)",
                    border: "2px solid var(--silver-150)",
                    fontWeight: "regular",
                    textAlign: "center",
                    color: getPriceColor(price.winter_wheel_4_price_store_login_honbu_flag),
                  }}
                >
                  {wheelPrice != null ? wheelPrice.toLocaleString() : ""}
                </TableCellComponent>
              )}

              {/* <TableCellComponent sx={{ p: 0, border: "none", textAlign: "center" }}>
                <ButtonComponent
                  label="本部価格に戻す"
                  sx={{
                    width: 200,
                    maxHeight: 48,
                    fontWeight: 600,
                    fontSize: { xs: 16, md: 18, lg: 20 },
                  }}
                  onClick={() => returnToHQSetting(index)}
                />
              </TableCellComponent> */}
            </TableRow>
          );
        })}
      </TableBody>
    </TableContainer>
  );
}
